import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  TemplateRef,
  ViewChild
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, Params, Router } from "@angular/router";
import { MainState } from "@app/store/state";
import { DeviceService } from "@core/services/device.service";
import { EquipmentItemService } from "@core/services/equipment-item.service";
import { LoadingService } from "@core/services/loading.service";
import { MobilePageMenuService } from "@core/services/mobile-page-menu.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import {
  EQUIPMENT_EXPLORER_PAGE_SORTING_COOKIE,
  ExplorerBaseComponent
} from "@features/equipment/pages/explorer-base/explorer-base.component";
import { EquipmentApiService, EquipmentItemsSortOrder } from "@features/equipment/services/equipment-api.service";
import {
  EquipmentActionTypes,
  GetAllBrands,
  GetAllBrandsSuccess,
  GetAllInBrand,
  GetAllInBrandSuccess,
  LoadBrand
} from "@features/equipment/store/equipment.actions";
import {
  arrayUniqueEquipmentItems,
  selectBrand,
  selectEquipmentItems
} from "@features/equipment/store/equipment.selectors";
import { BrandInterface } from "@features/equipment/types/brand.interface";
import { EquipmentItemBaseInterface, EquipmentItemType } from "@features/equipment/types/equipment-item-base.interface";
import { EquipmentItem } from "@features/equipment/types/equipment-item.type";
import { EquipmentListingsInterface } from "@features/equipment/types/equipment-listings.interface";
import { MatchType } from "@features/search/enums/match-type.enum";
import { NgbModal, NgbModalRef, NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { VariantSelectorModalComponent } from "@shared/components/equipment/item-browser/variant-selector-modal/variant-selector-modal.component";
import { CookieService } from "ngx-cookie";
import { filter, map, take, takeUntil } from "rxjs/operators";

@Component({
  selector: "astrobin-brand-explorer-page",
  templateUrl: "./brand-explorer-page.component.html",
  styleUrls: ["./brand-explorer-page.component.scss"]
})
export class BrandExplorerPageComponent extends ExplorerBaseComponent implements OnInit, OnDestroy {
  readonly ExplorerPageSortOrder = EquipmentItemsSortOrder;
  readonly UtilsService = UtilsService;

  title = this.translateService.instant("Brands");
  activeId: BrandInterface["id"];
  activeBrand: BrandInterface;
  itemsInBrand: EquipmentItemBaseInterface[];
  listings: EquipmentListingsInterface = null;

  // Templates for mobile menu
  @ViewChild("titleTemplate", { static: true })
  titleTemplate: TemplateRef<any>;

  @ViewChild("descriptionTemplate", { static: true })
  descriptionTemplate: TemplateRef<any>;

  @ViewChild("navTemplate", { static: true })
  navTemplate: TemplateRef<any>;
  protected readonly MatchType = MatchType;

  // Pagination properties
  currentPage = 1;
  totalCount = 0;
  paginatedBrands: BrandInterface[] = [];

  constructor(
    public readonly store$: Store<MainState>,
    public readonly actions$: Actions,
    public readonly activatedRoute: ActivatedRoute,
    public readonly router: Router,
    public readonly windowRefService: WindowRefService,
    public readonly translateService: TranslateService,
    public readonly cookieService: CookieService,
    public readonly loadingService: LoadingService,
    public readonly equipmentItemService: EquipmentItemService,
    public readonly modalService: NgbModal,
    public readonly equipmentApiService: EquipmentApiService,
    public readonly changeDetectionRef: ChangeDetectorRef,
    @Inject(PLATFORM_ID) public readonly platformId: Object,
    public readonly deviceService: DeviceService,
    public readonly offcanvasService: NgbOffcanvas,
    private readonly mobilePageMenuService: MobilePageMenuService
  ) {
    super(
      store$,
      actions$,
      activatedRoute,
      router,
      windowRefService,
      cookieService,
      changeDetectionRef,
      platformId,
      deviceService,
      offcanvasService
    );
    this.activeType = "BRAND";
  }

  get logo(): string {
    return (this.activeBrand.logo as string) || "/assets/images/brand-placeholder.png?v=2";
  }

  ngOnInit(): void {
    // Initialize parameters before calling super.ngOnInit()
    // which will call getItems()
    this._setParams();
    this._initPageFromUrl();

    super.ngOnInit();

    this._setupRouterEvents();
    this._registerMobilePageMenu();
  }

  getItems() {
    this.sortOrder =
      (this.cookieService.get(EQUIPMENT_EXPLORER_PAGE_SORTING_COOKIE) as EquipmentItemsSortOrder) ||
      EquipmentItemsSortOrder.AZ;

    // If we have a brandId in the URL, we're viewing a specific brand
    // The brand details will be loaded by _setParams() subscription
    if (!!this.activeId) {
      return;
    }

    if (!!this.activeBrand) {
      this.loadingService.setLoading(true);
      return this._loadItemsInBrand();
    }

    // For brands list, load the current page
    this._loadBrandsPage(this.currentPage);
  }

  viewBrand(brand: BrandInterface) {
    void this.router.navigateByUrl(`/equipment/explorer/brand/${brand.id}/${UtilsService.slugify(brand.name)}`);
  }

  viewItem(item: EquipmentItemBaseInterface): void {
    if (item.variants?.length > 0) {
      const modal: NgbModalRef = this.modalService.open(VariantSelectorModalComponent);
      const componentInstance: VariantSelectorModalComponent = modal.componentInstance;
      componentInstance.variants = [...[item], ...item.variants].filter(variant => !variant.frozenAsAmbiguous);
      componentInstance.enableSelectFrozen = false;

      modal.closed.pipe(take(1)).subscribe((variant: EquipmentItem) => {
        void this.router.navigateByUrl(`/equipment/explorer/${variant.klass}/${variant.id}`);
      });
    } else {
      void this.router.navigateByUrl(`/equipment/explorer/${item.klass}/${item.id}`);
    }
  }

  closeBrand() {
    void this.router.navigateByUrl("/equipment/explorer/brand");
  }

  ngOnDestroy(): void {
    // Clear the mobile page menu when component is destroyed
    this.mobilePageMenuService.clearMenu();
    super.ngOnDestroy();
  }

  // Handle mobile menu events
  onMobileMenuOpen(): void {
    super.onMobileMenuOpen();
  }

  onMobileMenuClose(): void {
    super.onMobileMenuClose();
  }

  private _loadBrandsPage(page: number) {
    // Set up subscription to handle the response for this specific page
    this.actions$
      .pipe(
        ofType(EquipmentActionTypes.GET_ALL_BRANDS_SUCCESS),
        takeUntil(this.destroyed$),
        take(1) // Only take the first response for this request
      )
      .subscribe((action: GetAllBrandsSuccess) => {
        this.paginatedBrands = action.payload.response.results;
        this.totalCount = action.payload.response.count;
        this.loadingService.setLoading(false);
      });

    // Dispatch the action to load brands for the specific page
    this.store$.dispatch(new GetAllBrands({ page, sort: this.sortOrder }));
  }

  pageChange(page: number) {
    this.currentPage = page;

    // Update URL with new page
    const queryParams: Params = { page };
    void this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: "merge"
    });

    // Scroll to top instantly for new pages
    this.windowRefService.scroll({ top: 0, left: 0, behavior: "auto" });

    // Clear old data immediately to show loading indicator
    this.paginatedBrands = [];

    this._loadBrandsPage(page);
  }

  // Override sorting methods to reset to page 1 and load immediately
  toggleAZSorting() {
    // Update sort order without calling super.getItems()
    if (this.sortOrder !== EquipmentItemsSortOrder.AZ) {
      this.sortOrder = EquipmentItemsSortOrder.AZ;
    } else {
      this.sortOrder = EquipmentItemsSortOrder.AZ_DESC;
    }

    this.cookieService.put(EQUIPMENT_EXPLORER_PAGE_SORTING_COOKIE, this.sortOrder, {
      path: "/",
      expires: null
    });

    this.currentPage = 1;
    this._updateUrlPage();

    // For brands list, clear data and show loading immediately
    if (!!this.activeBrand) {
      this._loadItemsInBrand();
    } else {
      this.paginatedBrands = []; // Clear old data immediately
      this._loadBrandsPage(this.currentPage);
    }
  }

  toggleUsersSorting() {
    // Update sort order without calling super.getItems() - descending first for numerical values
    if (this.sortOrder !== EquipmentItemsSortOrder.USERS_DESC) {
      this.sortOrder = EquipmentItemsSortOrder.USERS_DESC;
    } else {
      this.sortOrder = EquipmentItemsSortOrder.USERS;
    }

    this.cookieService.put(EQUIPMENT_EXPLORER_PAGE_SORTING_COOKIE, this.sortOrder, {
      path: "/",
      expires: null
    });

    this.currentPage = 1;
    this._updateUrlPage();

    // For brands list, clear data and show loading immediately
    if (!!this.activeBrand) {
      this._loadItemsInBrand();
    } else {
      this.paginatedBrands = []; // Clear old data immediately
      this._loadBrandsPage(this.currentPage);
    }
  }

  toggleImagesSorting() {
    // Update sort order without calling super.getItems() - descending first for numerical values
    if (this.sortOrder !== EquipmentItemsSortOrder.IMAGES_DESC) {
      this.sortOrder = EquipmentItemsSortOrder.IMAGES_DESC;
    } else {
      this.sortOrder = EquipmentItemsSortOrder.IMAGES;
    }

    this.cookieService.put(EQUIPMENT_EXPLORER_PAGE_SORTING_COOKIE, this.sortOrder, {
      path: "/",
      expires: null
    });

    this.currentPage = 1;
    this._updateUrlPage();

    // For brands list, clear data and show loading immediately
    if (!!this.activeBrand) {
      this._loadItemsInBrand();
    } else {
      this.paginatedBrands = []; // Clear old data immediately
      this._loadBrandsPage(this.currentPage);
    }
  }

  private _updateUrlPage() {
    const queryParams: Params = { page: this.currentPage };
    void this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: "merge"
    });
  }

  private _initPageFromUrl() {
    const pageParam = this.activatedRoute.snapshot.queryParamMap.get("page");
    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (page > 0) {
        this.currentPage = page;
      }
    }
  }

  private _setParams() {
    this.activeId = parseInt(this.activatedRoute.snapshot?.paramMap.get("brandId"), 10);
    if (!!this.activeId) {
      // Dispatch action to ensure brand is loaded into store
      this.store$.dispatch(new LoadBrand({ id: this.activeId }));

      this.store$
        .select(selectBrand, this.activeId)
        .pipe(
          filter(brand => !!brand),
          take(1)
        )
        .subscribe(brand => {
          this.activeBrand = brand;
          this._loadItemsInBrand();
          this._loadListings();
        });
    }
  }

  private _loadItemsInBrand() {
    if (!this.activeBrand) {
      return;
    }

    this.actions$
      .pipe(
        ofType(EquipmentActionTypes.GET_ALL_IN_BRAND_SUCCESS),
        takeUntil(this.destroyed$),
        map((action: GetAllInBrandSuccess) => action.payload.response)
      )
      .subscribe(response => {
        if (!response.next) {
          this.loadingService.setLoading(false);
          return;
        }

        const nextUrl = response.next;
        const nextPage = parseInt(UtilsService.getUrlParam(nextUrl, "page"), 10);
        this.store$.dispatch(
          new GetAllInBrand({ brand: this.activeBrand.id, type: response.results[0].klass, page: nextPage })
        );
      });

    for (const itemType of Object.keys(EquipmentItemType)) {
      this.store$.dispatch(
        new GetAllInBrand({ brand: this.activeBrand.id, type: itemType as EquipmentItemType, page: 1 })
      );

      this.store$
        .select(selectEquipmentItems)
        .pipe(
          takeUntil(this.destroyed$),
          map(items => items.filter(item => item.brand === this.activeBrand.id && item.klass === itemType)),
          filter(items => items.length > 0)
        )
        .subscribe(items => {
          if (!this.itemsInBrand) {
            this.itemsInBrand = [];
          }

          this.itemsInBrand = arrayUniqueEquipmentItems([...this.itemsInBrand, ...items]).sort(
            (a: EquipmentItemBaseInterface, b: EquipmentItemBaseInterface) => {
              let diff: number;

              switch (this.sortOrder) {
                case EquipmentItemsSortOrder.AZ:
                  return a.name.localeCompare(b.name);
                case EquipmentItemsSortOrder.AZ_DESC:
                  return b.name.localeCompare(a.name);
                case EquipmentItemsSortOrder.IMAGES:
                  diff = a.imageCount - b.imageCount;
                  return diff === 0 ? a.name.localeCompare(b.name) : diff;
                case EquipmentItemsSortOrder.IMAGES_DESC:
                  diff = b.imageCount - a.imageCount;
                  return diff === 0 ? a.name.localeCompare(b.name) : diff;
                case EquipmentItemsSortOrder.USERS:
                  diff = a.userCount - b.userCount;
                  return diff === 0 ? a.name.localeCompare(b.name) : diff;
                case EquipmentItemsSortOrder.USERS_DESC:
                  diff = b.userCount - a.userCount;
                  return diff === 0 ? a.name.localeCompare(b.name) : diff;
              }

              return 0;
            }
          );
        });
    }
  }

  private _setupRouterEvents() {
    this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this._setParams();
      });
  }

  private _loadListings() {
    this.loadingService.setLoading(true);

    this.listings = null;

    if (!!this.activeBrand) {
      this.equipmentApiService.getListingsForBrand(this.activeBrand.id).subscribe(listings => {
        // Skip "Lite" retailer integration for now. On the classic website, full integration means banners, and lite
        // integration means shopping cart menu in the corner of the technical card.
        // On the equipment item page, full means banners, and lite means stock availability.
        if (listings.allowFullRetailerIntegration) {
          this.listings = listings;
        }
        this.loadingService.setLoading(false);
      });
    } else {
      this.loadingService.setLoading(false);
    }
  }

  /**
   * Register the mobile page menu with the service
   */
  private _registerMobilePageMenu(): void {
    if (!this.deviceService.mdMax()) {
      return;
    }

    // Only register if the templates are available
    if (!this.titleTemplate || !this.descriptionTemplate || !this.navTemplate) {
      return;
    }

    // Register the menu configuration with the service
    // Use a unique ID to prevent duplicate registrations
    this.mobilePageMenuService.registerMenu(
      {
        titleTemplate: this.titleTemplate,
        descriptionTemplate: this.descriptionTemplate,
        template: this.navTemplate
      },
      "brand-explorer"
    );
  }
}
